import './Dialog.sass';

import React, { FC, useMemo } from 'react';
import { Link } from 'react-router5';
import { useSelector } from 'react-redux';

import { DialogProps } from '$core/Im/Dialog/Dialog.interface';
import { useDateShort, useTranslator, useUserInfo } from '$hooks';
import { imSelector, imTypingSelector } from '$store/im';
import { CHAT } from '$shared/constants/pages';
import { Badge } from '$uikit';
import {
  SpokenLanguage,
  Typing,
  UnitName,
  UnitPhoto,
  Verified,
} from '$shared/components';
import { RootState } from '$store/rootReducer';
import { AttachType } from '$store/models';

export const Dialog: FC<DialogProps> = ({ dialog }) => {
  const { history } = useSelector(imSelector);
  const peer = useUserInfo(dialog.peerId);
  const message = history[dialog.peerId]
    ? history[dialog.peerId][dialog.lastMsgId]
    : null;
  const t = useTranslator();
  const time = useDateShort(message?.createdAt ?? dialog.createdAt);
  const isTyping = useSelector((root: RootState) =>
    imTypingSelector(root, dialog.peerId),
  );

  const text = useMemo(() => {
    let result = message?.text ?? '';

    if (message && message.attachments && message.attachments.length > 0) {
      if (message.attachments[0].type === AttachType.VALENTINE_DAY_STICKER) {
        return t('dialog_attach_valentine_day_sticker');
      } else if (message.attachments[0].type === AttachType.GIFT) {
        return t('dialog_attach_gift');
      } else if (message.attachments.length > 1) {
        result += ' ' + t('dialog_attach_mixed');
      } else if (message.attachments[0].type === 'photo') {
        result += ' ' + t('dialog_attach_photo');
      } else if (message.attachments[0].type === 'video') {
        result += ' ' + t('dialog_attach_video');
      } else if (message.attachments[0].type === 'tips') {
        result += ' ' + t('dialog_attach_tips');
      }
    }

    const prefix = !message?.isInbox ? t('dialog_you') + ' ' : '';
    return prefix + result;
  }, [message?.text, message?.attachments, t]);

  return (
    <Link routeName={CHAT} routeParams={{ peerId: dialog.peerId }} className="Dialog">
      <UnitPhoto unit={peer} />
      <div className="Dialog__cont">
        <div className="Dialog__name_wrap">
          <div className="Dialog__name">
            <UnitName unit={peer} />
          </div>
          <Verified sponsor={dialog.sponsor} objectId={peer?.id} />
          <SpokenLanguage user={peer} />
        </div>
        <div className="Dialog__message">
          {isTyping ? <Typing /> : <div className="Dialog__text">{text}</div>}
          <div className="Dialog__date">{time}</div>
        </div>
      </div>
      {dialog.unread > 0 && (
        <div className="Dialog__unread">
          <Badge count={dialog.unread} />
        </div>
      )}
    </Link>
  );
};
